import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Trip } from "./useTrips";
import { toast } from "sonner";

// Types for Unsplash photo data
export interface UnsplashPhoto {
  id: string;
  url: string;
  blurHash: string | null;
  altDescription: string | null;
  photographerName: string;
  photographerUsername: string;
}

interface SearchResult {
  photos: UnsplashPhoto[];
  total: number;
  totalPages: number;
  page: number;
}

// Cover photo data extracted from a Trip
export interface TripCoverPhoto {
  id: string;
  url: string;
  blurHash: string | null;
  altDescription: string | null;
  photographerName: string;
  photographerUsername: string;
  positionY: number;
}

// Image size presets for dynamic Unsplash sizing
type ImageSize = 'hero' | 'thumbnail' | 'pdf' | 'preview';

const IMAGE_SIZES: Record<ImageSize, { w: number; h: number }> = {
  hero: { w: 1200, h: 400 },
  thumbnail: { w: 400, h: 267 },
  pdf: { w: 800, h: 300 },
  preview: { w: 1200, h: 256 }, // For preview page hero image
};

/**
 * Get a sized URL from a raw Unsplash URL.
 * Appends width, height, crop, and format parameters.
 */
export function getCoverPhotoUrl(rawUrl: string | null, size: ImageSize): string | null {
  if (!rawUrl) return null;

  // Defensive: fallback to hero size if invalid size provided
  const sizeConfig = IMAGE_SIZES[size] || IMAGE_SIZES.hero;
  const { w, h } = sizeConfig;
  const separator = rawUrl.includes('?') ? '&' : '?';
  return `${rawUrl}${separator}w=${w}&h=${h}&fit=crop&auto=format`;
}

/**
 * Extract cover photo data from a Trip object.
 */
export function extractCoverPhoto(trip: Trip): TripCoverPhoto | null {
  if (!trip.cover_photo_id || !trip.cover_photo_url) {
    return null;
  }
  return {
    id: trip.cover_photo_id,
    url: trip.cover_photo_url,
    blurHash: trip.cover_photo_blur_hash || null,
    altDescription: trip.cover_photo_alt_description || null,
    photographerName: trip.cover_photo_photographer_name || 'Unknown',
    photographerUsername: trip.cover_photo_photographer_username || 'unsplash',
    positionY: trip.cover_photo_position_y ?? 50,
  };
}

/**
 * Hook to search Unsplash photos.
 */
export function useSearchUnsplash(query: string, page: number = 1, enabled: boolean = true) {
  return useQuery({
    queryKey: ['unsplash-search', query, page],
    queryFn: async (): Promise<SearchResult> => {
      if (!query.trim()) {
        return { photos: [], total: 0, totalPages: 0, page: 1 };
      }

      const { data, error } = await supabase.functions.invoke('search-unsplash', {
        body: { query, page, perPage: 12 },
      });

      // Debug logging to see actual response
      console.log('Unsplash search response:', { data, error, query });

      if (error) {
        console.error('Error searching Unsplash:', error);
        throw new Error('Failed to search photos');
      }

      // Validate response structure
      if (!data || !Array.isArray(data.photos)) {
        console.error('Invalid response structure:', data);
        throw new Error('Invalid response from photo search');
      }

      return data as SearchResult;
    },
    enabled: enabled && !!query.trim(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Hook to set a trip's cover photo.
 */
export function useSetTripCoverPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tripId,
      photo,
    }: {
      tripId: string;
      photo: UnsplashPhoto;
    }) => {
      // First, trigger the Unsplash download endpoint (required by API guidelines)
      // This is fire-and-forget - we don't wait for it
      supabase.functions.invoke('trigger-unsplash-download', {
        body: { photoId: photo.id },
      }).catch((err) => {
        console.warn('Failed to trigger Unsplash download:', err);
      });

      // Update the trip with the new cover photo
      const { error } = await supabase
        .from('trips')
        .update({
          cover_photo_id: photo.id,
          cover_photo_url: photo.url,
          cover_photo_blur_hash: photo.blurHash,
          cover_photo_alt_description: photo.altDescription,
          cover_photo_photographer_name: photo.photographerName,
          cover_photo_photographer_username: photo.photographerUsername,
        })
        .eq('id', tripId);

      if (error) throw error;

      return { tripId, photo };
    },
    onSuccess: ({ tripId }) => {
      queryClient.invalidateQueries({ queryKey: ['trip', tripId] });
      queryClient.invalidateQueries({ queryKey: ['trips'] });
      toast.success('Cover photo updated');
    },
    onError: (error) => {
      console.error('Failed to set cover photo:', error);
      toast.error('Failed to update cover photo');
    },
  });
}

/**
 * Hook to remove a trip's cover photo.
 */
export function useRemoveTripCoverPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tripId: string) => {
      const { error } = await supabase
        .from('trips')
        .update({
          cover_photo_id: null,
          cover_photo_url: null,
          cover_photo_blur_hash: null,
          cover_photo_alt_description: null,
          cover_photo_photographer_name: null,
          cover_photo_photographer_username: null,
          cover_photo_position_y: null,
        })
        .eq('id', tripId);

      if (error) throw error;
      return tripId;
    },
    onSuccess: (tripId) => {
      queryClient.invalidateQueries({ queryKey: ['trip', tripId] });
      queryClient.invalidateQueries({ queryKey: ['trips'] });
      toast.success('Cover photo removed');
    },
    onError: (error) => {
      console.error('Failed to remove cover photo:', error);
      toast.error('Failed to remove cover photo');
    },
  });
}

/**
 * Hook to update the cover photo position.
 */
export function useUpdateCoverPhotoPosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tripId,
      positionY,
    }: {
      tripId: string;
      positionY: number;
    }) => {
      const { error } = await supabase
        .from('trips')
        .update({
          cover_photo_position_y: positionY,
        })
        .eq('id', tripId);

      if (error) throw error;
      return { tripId, positionY };
    },
    onSuccess: ({ tripId }) => {
      queryClient.invalidateQueries({ queryKey: ['trip', tripId] });
      queryClient.invalidateQueries({ queryKey: ['trips'] });
    },
    onError: (error) => {
      console.error('Failed to update cover photo position:', error);
      toast.error('Failed to update cover photo position');
    },
  });
}
